import { FC } from 'react';
import { Platform, PlatformTotals } from '../mock/types';
import { fmtCurrency, fmtDecimal, fmtInt, fmtPct } from '../utils';

const GoogleLogo: FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' aria-hidden>
    <path fill='#4285F4' d='M21.6 12.227c0-.673-.06-1.32-.173-1.94H12v3.67h5.39a4.61 4.61 0 01-2 3.027v2.516h3.235c1.894-1.745 2.985-4.314 2.985-7.273z' />
    <path
      fill='#34A853'
      d='M12 22c2.7 0 4.964-.895 6.62-2.42l-3.236-2.516c-.897.6-2.04.96-3.384.96-2.605 0-4.81-1.76-5.598-4.124H3.063v2.6A9.997 9.997 0 0012 22z'
    />
    <path fill='#FBBC05' d='M6.402 13.9A6.01 6.01 0 016.078 12c0-.66.116-1.3.324-1.9V7.5H3.063A9.997 9.997 0 002 12c0 1.614.387 3.14 1.063 4.5l3.34-2.6z' />
    <path
      fill='#EA4335'
      d='M12 5.977c1.466 0 2.78.504 3.815 1.49l2.864-2.864C16.96 3.005 14.697 2 12 2 8.094 2 4.71 4.245 3.063 7.5l3.34 2.6C7.19 7.737 9.395 5.977 12 5.977z'
    />
  </svg>
);

const MetaLogo: FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox='0 0 64 36' aria-hidden>
    <defs>
      <linearGradient id='meta-grad-tbl' x1='0%' y1='0%' x2='100%' y2='100%'>
        <stop offset='0%' stopColor='#0064E0' />
        <stop offset='100%' stopColor='#1877F2' />
      </linearGradient>
    </defs>
    <path
      fill='url(#meta-grad-tbl)'
      d='M9 6c5.5 0 9 4.8 12 11 3-6.2 6.5-11 12-11 6 0 9.5 5.5 9.5 13s-3.5 13-9.5 13c-3.6 0-6.3-2.7-8.7-6.7-1.2 1.8-2.4 3.5-3.7 4.9C18.2 32.5 14 30 11.7 25.8 9.4 21.6 9 17.5 9 13c0-4 1.5-7 5-7zm22.9 6.5c-3.5 4.5-5.8 9.4-7.5 14.3 1.7 3.4 3.6 5.6 6.4 5.6 4.5 0 6.7-3.9 6.7-9.4s-2.1-10.5-5.6-10.5z'
    />
  </svg>
);

type Row = {
  label: string;
  getter: (p: PlatformTotals | undefined, otherPresent: boolean) => { text: string; muted?: boolean };
};

const dash = (text = '— non disponibile'): { text: string; muted: boolean } => ({ text, muted: true });

const ROWS: Row[] = [
  {
    label: 'Impression',
    getter: (p) => (p ? { text: fmtInt(p.impressions) } : dash()),
  },
  {
    label: 'Reach (utenti unici)',
    getter: (p) => (p && p.reach !== null ? { text: fmtInt(p.reach) } : dash()),
  },
  {
    label: 'Frequenza',
    getter: (p) => (p && p.frequency !== null ? { text: fmtDecimal(p.frequency) } : dash()),
  },
  {
    label: 'Click',
    getter: (p) => (p ? { text: fmtInt(p.clicks) } : dash()),
  },
  {
    label: 'CTR',
    getter: (p) => (p ? { text: fmtPct(p.ctr) } : dash()),
  },
  {
    label: 'CPC medio',
    getter: (p) => (p ? { text: fmtCurrency(p.cpc, p.currencyCode) } : dash()),
  },
  {
    label: 'Costo totale',
    getter: (p) => (p ? { text: fmtCurrency(p.cost, p.currencyCode) } : dash()),
  },
  {
    label: 'Visite in negozio',
    getter: (p, otherPresent) => (p && p.storeVisits !== null ? { text: fmtInt(p.storeVisits) } : otherPresent ? dash('n/a') : dash()),
  },
];

type ComparisonTableProps = {
  platforms: PlatformTotals[];
  visiblePlatforms: Platform[];
};

export const ComparisonTable: FC<ComparisonTableProps> = ({ platforms, visiblePlatforms }) => {
  const google = platforms.find((p) => p.platform === 'google');
  const meta = platforms.find((p) => p.platform === 'meta');
  const showGoogle = visiblePlatforms.includes('google');
  const showMeta = visiblePlatforms.includes('meta');

  return (
    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5 }}>
      <thead>
        <tr>
          <th style={thStyle('left')}>Metrica</th>
          {showGoogle ? (
            <th style={thStyle('left')}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <GoogleLogo />
                Google Ads
              </span>
            </th>
          ) : null}
          {showMeta ? (
            <th style={thStyle('right')}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <MetaLogo />
                Meta
              </span>
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {ROWS.map((row, i) => {
          const isLast = i === ROWS.length - 1;
          const gCell = row.getter(google, !!meta && showMeta);
          const mCell = row.getter(meta, !!google && showGoogle);
          return (
            <tr key={row.label}>
              <td style={tdLabelStyle(isLast)}>{row.label}</td>
              {showGoogle ? <td style={tdStyle(isLast, gCell.muted)}>{gCell.muted ? <MutedCell text={gCell.text} /> : gCell.text}</td> : null}
              {showMeta ? <td style={tdStyle(isLast, mCell.muted)}>{mCell.muted ? <MutedCell text={mCell.text} /> : mCell.text}</td> : null}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const MutedCell: FC<{ text: string }> = ({ text }) => (
  <>
    <span style={{ fontSize: 16, color: '#C6CBD4' }}>—</span> <span style={{ fontSize: 11.5 }}>{text.replace(/^—\s*/, '')}</span>
  </>
);

const thStyle = (align: 'left' | 'right'): React.CSSProperties => ({
  textAlign: 'left',
  fontSize: 11.5,
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  fontWeight: 600,
  color: '#7A8597',
  padding: '8px 14px',
  background: '#F7F8FA',
  borderTopLeftRadius: align === 'left' ? 8 : 0,
  borderBottomLeftRadius: align === 'left' ? 8 : 0,
  borderTopRightRadius: align === 'right' ? 8 : 0,
  borderBottomRightRadius: align === 'right' ? 8 : 0,
});

const tdLabelStyle = (isLast: boolean): React.CSSProperties => ({
  padding: '10px 14px',
  borderBottom: isLast ? 'none' : '1px solid #F0F2F5',
  color: '#344155',
  fontVariantNumeric: 'tabular-nums',
});

const tdStyle = (isLast: boolean, muted?: boolean): React.CSSProperties => ({
  padding: '10px 14px',
  borderBottom: isLast ? 'none' : '1px solid #F0F2F5',
  color: muted ? '#9CA3B1' : '#0B1733',
  fontWeight: muted ? 400 : 600,
  fontVariantNumeric: 'tabular-nums',
});
